import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { Utils } from '@treezor/design-system';

import { TCoreAction } from '@/store/types/modals.types';
import { TAppError } from '@/store/types/base.state';
import { IBulkCard } from '@/features/cards/store/models/bulk-card.model';
import {
  TBulkCardCreationsAction,
  BulkCardCreationsActionTypes,
  IBulkCardCreationsState,
} from '@/features/cards/store/types/bulk-cards-creation.types';
import { BulkCardService } from '@/services/BulkCardService';
import { closeModal } from '@/store/actions/core.actions';

export const getBulkCardCreations = (): ThunkAction<
  Promise<void>,
  IBulkCardCreationsState,
  {},
  TBulkCardCreationsAction | TCoreAction
> => {
  const request = (): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATIONS,
  });
  const fail = (error: TAppError): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATIONS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/creation.html#physical-card-creation',
  });
  const success = (bulkCardCreations: IBulkCard[]): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATIONS_SUCCESS,
    shouldSkipToaster: true,
    bulkCardCreations,
  });

  return async (
    dispatch: ThunkDispatch<IBulkCardCreationsState, {}, TBulkCardCreationsAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    try {
      const bulkCardCreations: IBulkCard[] = await BulkCardService.getBulkCards();
      dispatch(success(bulkCardCreations));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setBulkCardCreationStep = (
  currentStep: number,
  bulkCardCreationDraft: IBulkCard | null = null,
): ThunkAction<Promise<void>, IBulkCardCreationsState, {}, TBulkCardCreationsAction | TCoreAction> => {
  const request = (): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.SET_STEP,
    currentStep,
    bulkCardCreationDraft,
  });
  return async (
    dispatch: ThunkDispatch<IBulkCardCreationsState, {}, TBulkCardCreationsAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    dispatch(closeModal());
  };
};

export const createBulkCard = (
  bulkCardData: IBulkCard,
): ThunkAction<Promise<void>, IBulkCardCreationsState, {}, TBulkCardCreationsAction | TCoreAction> => {
  const request = (): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.CREATE_BULK_CARD,
  });
  const fail = (error: TAppError): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.CREATE_BULK_CARD_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/creation.html#physical-card-creation',
  });
  const success = (bulkCard: IBulkCard): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.CREATE_BULK_CARD_SUCCESS,
    bulkCard,
  });

  return async (dispatch: ThunkDispatch<IBulkCardCreationsState, {}, TBulkCardCreationsAction>): Promise<void> => {
    dispatch(request());
    try {
      const bulkCard = await BulkCardService.createBulkCard(bulkCardData);
      dispatch(success(bulkCard));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const clearbulkCardCreationDraft = (): ThunkAction<
  Promise<void>,
  IBulkCardCreationsState,
  {},
  TBulkCardCreationsAction
> => {
  const request = (): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.CLEAR_BULK_CARD_CREATION_CACHE,
  });
  return async (dispatch: ThunkDispatch<IBulkCardCreationsState, {}, TBulkCardCreationsAction>): Promise<void> => {
    dispatch(request());
  };
};

export const getBulkCardCreationReport = (
  orderId: string,
): ThunkAction<Promise<void>, string, {}, TBulkCardCreationsAction> => {
  const request = (): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATION_REPORT,
  });
  const fail = (error: TAppError): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATION_REPORT_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/cards/creation.html#physical-card-creation',
  });
  const success = (bulkCardCreationReport: any): TBulkCardCreationsAction => ({
    type: BulkCardCreationsActionTypes.GET_BULK_CARD_CREATION_REPORT_SUCCESS,
    bulkCardCreationReport,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<string, {}, TBulkCardCreationsAction>): Promise<void> => {
    dispatch(request());
    try {
      const bulkReport = await BulkCardService.getBulkCardReport(orderId);

      Utils.downloadFile(bulkReport.link, `${orderId}_bulkcard_report.csv`);

      dispatch(success(bulkReport));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
